import { Delete, DeleteOutline, Edit, EditOutlined } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Pagination,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer as MuiTableContainer,
  TableRow,
  Typography,
  Tooltip,
} from "@mui/material";
import { AdvancedConfirmDialog, NoData, StyledTableHead, TableContainer } from "components";
import { LangConstant } from "const";
import { useChannelDelete } from "hooks";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UpdateChannelDialog from "./UpdateChannelDialog";

const VIEW_MODE = {
  edit: "edit",
  delete: "delete",
};

const ChannelListTable = ({ channels, totalChannels, page, totalPages, isFetching, onChangePage }) => {
  const { t: getLabel } = useTranslation();
  const { t: getCommonLabel } = useTranslation(LangConstant.NS_COMMON);

  const { deleteChannel } = useChannelDelete();

  const tableLabels = getLabel(LangConstant.OBJ_CHANNEL_LIST_TABLE, { returnObjects: true });

  const [viewMode, setViewMode] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const columns = useMemo(
    () => [
      { label: tableLabels.channelName, align: "left" },
      { label: tableLabels.memberCount, align: "left", width: "20%" },
      { label: tableLabels.action, align: "center", width: "10%" },
    ],
    [tableLabels],
  );

  const handleDeleteMode = channel => () => {
    setSelectedChannel(channel);
    setViewMode(VIEW_MODE.delete);
  };

  const handleEditMode = channel => () => {
    setSelectedChannel(channel);
    setViewMode(VIEW_MODE.edit);
  };

  const handleReset = () => {
    setViewMode(null);
    setSelectedChannel(null);
  };

  const handleConfirmDelete = () => {
    deleteChannel({
      groupId: selectedChannel.id,
      onSuccess: handleReset,
    });
  };

  return (
    <Stack spacing={1.5} flex={1} minHeight={0}>
      <Typography style={{ fontSize: 20, fontWeight: 700 }}>
        {getLabel(LangConstant.FM_TOTAL_CHANNELS, { number: totalChannels })}
      </Typography>

      <TableContainer>
        <Table stickyHeader>
          <StyledTableHead columns={columns} />
          <TableBody>
            {channels.map(channel => (
              <TableRow key={channel.id} hover>
                <TableCell align="left">{channel.groupName}</TableCell>
                <TableCell align="left">{channel.memberCount}</TableCell>
                <TableCell align="center">
                  <Tooltip title={getLabel("TXT_EDIT_INFO")}>
                    <span>
                      <IconButton size="small" color="primary" onClick={handleEditMode(channel)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={getLabel("TXT_DISSOLVE_CHANNEL")}>
                    <span>
                      <IconButton size="small" color="error" onClick={handleDeleteMode(channel)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!isFetching && channels.length === 0 && <NoData content={getCommonLabel("TXT_NO_CHANNEL")} />}
      </TableContainer>

      <PaginationBox>
        <Pagination
          count={totalPages}
          page={page}
          onChange={onChangePage}
          shape="rounded"
          color="primary"
          variant="outlined"
        />
      </PaginationBox>

      {viewMode === VIEW_MODE.delete && selectedChannel && (
        <AdvancedConfirmDialog
          open
          title={getCommonLabel("TXT_DISSOLVE_CHANNEL")}
          content={getCommonLabel("FM_DISSOLVE_CHANNEL_CONTENT", { groupName: selectedChannel.groupName })}
          submitProps={{
            submitText: getCommonLabel("L_CONFIRM_BUTTON"),
            onClick: handleConfirmDelete,
          }}
          cancelProps={{
            onClick: handleReset,
          }}
        />
      )}

      {viewMode === VIEW_MODE.edit && selectedChannel && (
        <UpdateChannelDialog open channel={selectedChannel} onClose={handleReset} />
      )}
    </Stack>
  );
};

export default ChannelListTable;

const PaginationBox = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  paddingTop: 16,
  paddingBottom: 8,
  flexShrink: 0,
});
